import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import type { Service } from '@/design-system';
import { inspectionApi } from '@/entities/inspection';
import { hasSurvey, orderKeys, type Order } from '@/entities/order';
import { reportKeys } from '@/entities/report';
import { useSyncState, useTenantDb } from '@/shared/offline';

import { prepareSurvey } from './prepareSurvey';

/** De opname die "Opname heropenen" openzet. */
export interface ReopenTarget {
    inspectionUuid: string;
    /** De dienst van de opname: een energielabel opent een ander scherm. */
    service: Service;
}

/**
 * De ingediende opname die deze sessie weer mag openzetten, of `null`.
 *
 * Wie dat mag - de eigenaar en de inspecteur van de opname, voor en na verzending,
 * nooit bij een geannuleerde keuring - zegt de API per opname (`can_reopen`); de app
 * rekent het niet na.
 */
export function reopenableSurvey(order: Pick<Order, 'services'>): ReopenTarget | null {
    const service = order.services.find(
        (candidate) => hasSurvey(candidate) && candidate.inspection.canReopen,
    );

    return service && service.inspection
        ? { inspectionUuid: service.inspection.uuid, service: service.service }
        : null;
}

/**
 * Heropenen en meteen de opname in: de API zet haar open, de synchronisatie brengt haar
 * bewerkbaar naar dit apparaat, en dan pas gaat het scherm erheen - zo kan er daarna
 * ook zonder verbinding verder gewerkt worden.
 *
 * Komt ze niet op tijd, dan gaan we toch: het opnamescherm zegt zelf dat ze wordt
 * klaargezet en opent vanzelf zodra ze er is. Zonder lokale opslag (meekijken in een
 * ander bedrijf) valt er niets op te halen; ook dat legt het scherm zelf uit.
 */
export function useReopenSurvey(order: Pick<Order, 'uuid'>) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const db = useTenantDb();
    const { flushNow } = useSyncState();
    const [preparing, setPreparing] = useState(false);

    const reopen = useMutation({
        mutationFn: (input: ReopenTarget & { reason: string }) =>
            inspectionApi.reopen(input.inspectionUuid, input.reason),
        onSuccess: async (_result, input) => {
            void queryClient.invalidateQueries({ queryKey: orderKeys.detail(order.uuid) });
            void queryClient.invalidateQueries({ queryKey: orderKeys.timeline(order.uuid) });
            void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
            void queryClient.invalidateQueries({ queryKey: reportKeys.forOrder(order.uuid) });

            if (db) {
                setPreparing(true);

                try {
                    await prepareSurvey({
                        db,
                        inspectionUuid: input.inspectionUuid,
                        sync: flushNow,
                        editable: true,
                    });
                } catch {
                    /* Niets kunnen ophalen is geen reden om de weg naar de opname te weigeren. */
                } finally {
                    setPreparing(false);
                }
            }

            void navigate(
                paths.inspectionFor({ uuid: input.inspectionUuid, service: input.service }),
            );
        },
    });

    return { reopen, preparing };
}
